import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import styles from "./GroupEntryModal.module.css";
import StudentTab from "../StudentTab/StudentTab";
import ActiveLessonTab from "../ActiveLessonTab/ActiveLessonTab";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import CopyFooter from "../CopyFooter/CopyFooter";
import LectureManager from "../LectureManager/LectureManager";
import Tests from "../Tests/Tests";
import { useApiError } from "../../utils/useApiError";
import ValidationErrorModal from "../ValidationErrorModal/ValidationErrorModal";
import StatistikaTab from "../StatistikaTab/StatistikaTab";
import GroupParentsTab from "../GroupParentsTab/GroupParentsTab";
import ReloadButton from "../LoadingButtons/ReloadButton";
import AppSpinner from "../Spinner/AppSpinner";


const GroupEntryModal = ({ show, handleClose, group }) => {
  const [isMaximized, setIsMaximized] = useState(true);
  const [activeTab, setActiveTab] = useState("Tələbələr");
  const [isOnline, setIsOnline] = useState(true);
  const [isStandart, setIsStandart] = useState(true);
  const [lessonActive, setLessonActive] = useState(false);
  const [lessonLoading, setLessonLoading] = useState(false);
  const { errorModal, clearError, handleApiError } = useApiError();

  const [lessonName, setLessonName] = useState("");
  const [lessonDate, setLessonDate] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  const [materialLink, setMaterialLink] = useState("");
  const [videoLink, setVideoLink] = useState("");

  const draftKeyRef = useRef(null);
  draftKeyRef.current = group?.id ? `lessonFormDraft_${group.id}` : null;

  const [copiedText, setCopiedText] = useState("");
  const [showHint, setShowHint] = useState(false);
  const hintTimer = useRef(null);
  const [showStartLessonConfirm, setShowStartLessonConfirm] = useState(false);

  const [helpHistoryData, setHelpHistoryData] = useState([]);
  const [helpHistoryLoading, setHelpHistoryLoading] = useState(false);
  const [helpHistoryTrigger, setHelpHistoryTrigger] = useState(null);
  const [historyBeginDate, setHistoryBeginDate] = useState(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [historyEndDate, setHistoryEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedHelpLesson, setSelectedHelpLesson] = useState(null);
  const [helpLessonCopiedText, setHelpLessonCopiedText] = useState("");

  const handleBackdropClick = () => {
    setShowHint(true);
    clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setShowHint(false), 2500);
  };

  const tabs = ["Tələbələr", "Statistika", "Dərslər", "Testlər", "Valideynlər", "Kömək dərslərim"];

  useEffect(() => {
    if (!show) setShowStartLessonConfirm(false);
  }, [show]);

  useEffect(() => {
    if (activeTab === "Kömək dərslərim") fetchHelpHistory("search");
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === "Kömək dərslərim") fetchHelpHistory("search");
  }, [historyBeginDate, historyEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (show && group?.id) {
      const key = `lessonFormDraft_${group.id}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          const draft = JSON.parse(saved);
          if (draft.lessonName !== undefined) setLessonName(draft.lessonName);
          if (draft.lessonDate !== undefined) setLessonDate(draft.lessonDate);
          if (draft.materialLink !== undefined) setMaterialLink(draft.materialLink);
          if (draft.videoLink !== undefined) setVideoLink(draft.videoLink);
        } catch (e) { console.error(e); }
      }

      const checkCurrentStatus = async () => {
        try {
          const res = await apiFetch(`${BASE_URL}/teacher-groups/${group.id}`);
          const data = await res.json();
          console.log(data);
          if (data.teacherGroup?.lessonStatus === "started") {
            setLessonActive(true);
          } else {
            setLessonActive(false);
          }
        } catch (err) {
          console.error("Status yoxlanarkən xəta:", err);
        }
      };
      checkCurrentStatus();
    }
  }, [show, group?.id]);

  useEffect(() => {
    if (!draftKeyRef.current) return;
    localStorage.setItem(draftKeyRef.current, JSON.stringify({ lessonName, lessonDate, materialLink, videoLink }));
  }, [lessonName, lessonDate, materialLink, videoLink]);

  const handleStartLesson = async () => {
    setLessonLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/teacher-groups/start-lesson`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          groupId: group?.id,
          lessonType: isStandart ? "Standart" : "Əlavə",
          trainingType: isOnline ? "Online" : "Offline",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        handleApiError({ message: data?.message ?? 'Dərs başladılmadı.', validations: data?.validations });
        return;
      }
      setLessonActive(true);
    } catch (err) {
      handleApiError({ message: 'Server xətası baş verdi.' });
    } finally {
      setLessonLoading(false);
    }
  };

  const openStartLessonConfirm = () => {
    setLessonDate(new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    setShowStartLessonConfirm(true);
  };

  const confirmAndStartLesson = async () => {
    setShowStartLessonConfirm(false);
    await handleStartLesson();
  };

  const handleEndLesson = () => {
    setLessonActive(false);
    setLessonName("");
    setMaterialLink("");
    setVideoLink("");
    setLessonDate(new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    setIsOnline(true);
    setIsStandart(true);
    if (draftKeyRef.current) localStorage.removeItem(draftKeyRef.current);
  };

  const handleCellClick = (text) => {
    setCopiedText(text?.toString() || "");
  };

  const fetchHelpHistory = useCallback(async (src = "search") => {
    const gId = group?.id;
    if (!gId || !historyBeginDate || !historyEndDate) return;
    setHelpHistoryTrigger(src);
    setHelpHistoryLoading(true);
    try {
      const [res] = await Promise.all([
        apiFetch(`${BASE_URL}/teacher-help-lessons/my-lessons?groupId=${gId}&beginDate=${historyBeginDate}&endDate=${historyEndDate}`),
        new Promise(r => setTimeout(r, 600)),
      ]);
      if (res.ok) {
        const data = await res.json();
        setHelpHistoryData(Array.isArray(data) ? data : []);
        setSelectedHelpLesson(null);
        setHelpLessonCopiedText("");
      }
    } catch {
    } finally {
      setHelpHistoryLoading(false);
      setHelpHistoryTrigger(null);
    }
  }, [group?.id, historyBeginDate, historyEndDate]);

  return (
    <Modal
      show={show}
      onHide={handleBackdropClick}
      fullscreen={isMaximized}
      size={isMaximized ? undefined : "xl"}
      centered={!isMaximized}
      /* Portal ilə açılan tələbə qeydi / müəllim qeydi dialoqları body-də olduğu üçün fokus tələbi onları sıradan çıxarır; əks halda select və textarea işləmir */
      enforceFocus={false}
className={styles.customModal}
      contentClassName={styles.modalContent}
      dialogClassName={isMaximized ? styles.modalDialogFullscreen : styles.modalDialogNormal}
    >
      <div className={`${styles.wrapper} ${isMaximized ? styles.wrapperFullscreen : ""}`}>

        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.brandIcon}>
              <i className="bi bi-mortarboard-fill"></i>
            </div>
            <div className={styles.headerTitle}>
              <h5>
                Qrup:{" "}
                <span style={{ color: "var(--primary)" }}>
                  {group?.name || "Qrup-00467"}
                </span>
              </h5>
            </div>
          </div>

          <nav className={styles.navTabs}>
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`${styles.navLink} ${activeTab === tab ? styles.activeNavLink : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </nav>

          <div className={styles.headerActions}>
            {lessonActive && (
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: "#059669",
                background: "rgba(5,150,105,0.1)",
                border: "1px solid rgba(5,150,105,0.3)",
                padding: "3px 10px", borderRadius: 20,
                display: "flex", alignItems: "center", gap: 5,
                whiteSpace: "nowrap",
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: "#059669",
                  animation: "pulse 1.5s infinite",
                  flexShrink: 0,
                }}></span>
                Dərs davam edir
              </span>
            )}
            {showHint && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: "var(--color-primary)",
                background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.25)",
                borderRadius: 8, padding: "4px 10px",
                animation: "hintFadeIn 0.2s ease",
                whiteSpace: "nowrap",
              }}>
           Bağlamaq üçün "×" işarəsinə toxunun
              </span>
            )}
            <button
              type="button"
              className={styles.actionCircle}
              title={isMaximized ? "Kiçilt" : "Tam ekran"}
              onClick={() => setIsMaximized(!isMaximized)}
            >
              <i className={`bi bi-${isMaximized ? "fullscreen-exit" : "fullscreen"}`}></i>
            </button>
            <button
              type="button"
              className={`${styles.actionCircle} ${styles.closeBtn}`}
              onClick={handleClose}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <style>{`@keyframes hintFadeIn { from { opacity: 0; transform: translateX(6px); } to { opacity: 1; transform: translateX(0); } }`}</style>
        </header>

        <main className={styles.mainBody}>
          <aside className={styles.sidebar}>
            <div className={styles.sideScrollArea}>
              <h6 className={styles.sidebarTitle}>Dərs parametrləri</h6>

              <Form.Group className="mb-3">
                <label className={styles.fieldLabel}>Dərsin adı</label>
                <Form.Control as="textarea" rows={3} className={styles.glassInput} value={lessonName} onChange={(e) => setLessonName(e.target.value)} />
              </Form.Group>

              <Form.Group className="mb-3">
                <label className={styles.fieldLabel}>Tarix</label>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Form.Control
                    type="datetime-local"
                    className={styles.glassInput}
                    value={lessonDate}
                    onChange={(e) => setLessonDate(e.target.value)}
                    style={{ width: 0, flex: 1, fontSize: 11, padding: "4px 6px" }}
                  />
                  <button
                    type="button"
                    title="İndiki vaxtı seç"
                    onClick={() => setLessonDate(new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16))}
                    style={{
                      flexShrink: 0,
                      width: 26,
                      height: 26,
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      background: "#f9fafb",
                      color: "#2563eb",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                    }}
                  >
                    <i className="bi bi-clock-fill"></i>
                  </button>
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <label className={styles.fieldLabel}>Material link</label>
                <Form.Control placeholder="https://" className={styles.glassInput} value={materialLink} onChange={(e) => setMaterialLink(e.target.value)} />
              </Form.Group>

              <Form.Group className="mb-3">
                <label className={styles.fieldLabel}>Video link</label>
                <Form.Control placeholder="https://" className={styles.glassInput} value={videoLink} onChange={(e) => setVideoLink(e.target.value)} />
              </Form.Group>

              <div className="mb-3">
                <label className={styles.fieldLabel}>Format</label>
                <div className={styles.segmentedControl}>
                  <button type="button" className={isOnline ? styles.segActiveBlue : ""} onClick={() => setIsOnline(true)}>Online</button>
                  <button type="button" className={!isOnline ? styles.segActiveGrey : ""} onClick={() => setIsOnline(false)}>Offline</button>
                </div>
              </div>

              <div className="mb-3">
                <label className={styles.fieldLabel}>Tip</label>
                <div className={styles.segmentedControl}>
                  <button type="button" className={isStandart ? styles.segActiveGreen : ""} onClick={() => setIsStandart(true)}>Standart</button>
                  <button type="button" className={!isStandart ? styles.segActiveOrange : ""} onClick={() => setIsStandart(false)}>Əlavə</button>
                </div>
              </div>
            </div>

            <div className={styles.sidebarFooter}>
              {lessonActive && (
                <div className="p-2 text-center small text-muted border rounded bg-light">
                  Dərs idarəetmə panelindədir
                </div>
              )}
            </div>
          </aside>

          <div className={styles.viewportWrapper}>
            <section className={styles.viewport}>
              {activeTab === "Tələbələr" && lessonActive && (
                <ActiveLessonTab
                  groupId={group?.id}
                  groupData={group}
                  onFinish={handleEndLesson}
                  onEndLesson={handleEndLesson}
                  styles={styles}
                  lessonName={lessonName}
                  lessonDate={lessonDate}
                  materialLink={materialLink}
                  videoLink={videoLink}
                  trainingType={isOnline ? "Online" : "Offline"}
                  lessonType={isStandart ? "Standart" : "Əlavə"}
                  onCellClick={handleCellClick}
                />
              )}
              {activeTab === "Tələbələr" && !lessonActive && (
                <StudentTab
                  groupId={group?.id}
                  styles={styles}
                  lessonLoading={lessonLoading}
                  onStartLesson={openStartLessonConfirm}
                  onCellClick={handleCellClick}
                />
              )}
              {activeTab === "Statistika" && (
                <StatistikaTab groupId={group?.id} />
              )}
              {activeTab === "Dərslər" && (
    <LectureManager groupId={group?.id} sector={group?.sector} />
              )}
              {activeTab === "Testlər" && (
                <Tests groupId={group?.id} totalStudents={group?.studentCount ?? 0} onCellClick={setCopiedText} />
              )}
              {activeTab === "Valideynlər" && (
                <GroupParentsTab groupId={group?.id} onCellClick={handleCellClick} />
              )}
              {activeTab === "Kömək dərslərim" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%", minHeight: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input type="date" value={historyBeginDate} onChange={e => setHistoryBeginDate(e.target.value)} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 10px", fontSize: 13, color: "#111827", outline: "none" }} />
                    <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>
                    <input type="date" value={historyEndDate} onChange={e => setHistoryEndDate(e.target.value)} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 10px", fontSize: 13, color: "#111827", outline: "none" }} />
                    <ReloadButton onClick={() => fetchHelpHistory("reload")} loading={helpHistoryTrigger === "reload"} disabled={helpHistoryLoading && helpHistoryTrigger !== "reload"} />
                  </div>

                  <div className={styles.helpLessonsRow} style={{ display: "flex", gap: 12, flex: 1, minHeight: 0 }}>
                    {/* Sol — dərslər siyahısı */}
                    <div className={styles.helpLessonsList} style={{ width: 260, flexShrink: 0, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "auto" }}>
                      {helpHistoryLoading ? (
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                          <AppSpinner text="Yüklənir..." />
                        </div>
                      ) : helpHistoryData.length === 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 8, color: "#9ca3af", padding: 16 }}>
                          <i className="bi bi-calendar-x" style={{ fontSize: 36 }}></i>
                          <span style={{ fontSize: 13, textAlign: "center" }}>Bu tarix aralığında kömək dərsi tapılmadı.</span>
                        </div>
                      ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: "#f3f4f6" }}>
                              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#374151", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #e5e7eb" }}>#</th>
                              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#374151", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #e5e7eb" }}>Dərs</th>
                            </tr>
                          </thead>
                          <tbody>
                            {helpHistoryData.map((row, idx) => {
                              const isSelected = selectedHelpLesson?.id === row.id;
                              return (
                                <tr key={row.id} onClick={() => { setSelectedHelpLesson(row); setHelpLessonCopiedText(""); }}
                                  style={{ cursor: "pointer", background: isSelected ? "rgba(37,99,235,0.08)" : idx % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #e5e7eb", transition: "background 0.15s" }}>
                                  <td style={{ padding: "7px 10px", color: "#9ca3af", fontWeight: 600 }}>{idx + 1}</td>
                                  <td style={{ padding: "7px 10px" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, minWidth: 0 }}>
                                      <div style={{ flex: 1, minWidth: 0, color: isSelected ? "var(--color-primary)" : "#111827", fontWeight: isSelected ? 700 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.lessonName || "—"}</div>
                                      {row.youtubeLink ? (
                                        <a href={row.youtubeLink} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 6, background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)", textDecoration: "none" }}>
                                          <i className="bi bi-play-circle-fill" style={{ fontSize: 11 }} />
                                        </a>
                                      ) : null}
                                    </div>
                                    {row.beginDate && (
                                      <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>
                                        {new Date(row.beginDate).toLocaleDateString("az-AZ", { day: "2-digit", month: "2-digit", year: "numeric" })}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Sağ — detal paneli */}
                    <div style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "auto" }}>
                      {!selectedHelpLesson ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 8, color: "#9ca3af", padding: 16 }}>
                          <i className="bi bi-hand-index-thumb" style={{ fontSize: 36 }}></i>
                          <span style={{ fontSize: 13 }}>Sol siyahıdan dərs seçin</span>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{selectedHelpLesson.lessonName || "—"}</span>
                            <span style={{
                              background: selectedHelpLesson.lessonType === "Online" ? "rgba(5,150,105,0.12)" : "rgba(239,68,68,0.12)",
                              color: selectedHelpLesson.lessonType === "Online" ? "#059669" : "#ef4444",
                              border: `1px solid ${selectedHelpLesson.lessonType === "Online" ? "rgba(5,150,105,0.3)" : "rgba(239,68,68,0.3)"}`,
                              borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4,
                            }}>
                              <i className={`bi bi-${selectedHelpLesson.lessonType === "Online" ? "wifi" : "building"}`} />
                              {selectedHelpLesson.lessonType}
                            </span>
                          </div>
                          <div style={{ display: "flex", gap: 16, padding: "10px 14px", borderBottom: "1px solid #e5e7eb", flexWrap: "wrap" }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                              Başlanğıc: <strong>{selectedHelpLesson.beginDate ? new Date(selectedHelpLesson.beginDate).toLocaleString("az-AZ", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</strong>
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                              Bitmə: <strong>{selectedHelpLesson.endDate ? new Date(selectedHelpLesson.endDate).toLocaleString("az-AZ", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</strong>
                            </span>
                            {selectedHelpLesson.register && (
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af" }}>
                                Qeydiyyat: <strong>{new Date(selectedHelpLesson.register).toLocaleDateString("az-AZ")}</strong>
                              </span>
                            )}
                          </div>
                          {(!selectedHelpLesson.students || selectedHelpLesson.students.length === 0) ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, gap: 8, color: "#9ca3af" }}>
                              <i className="bi bi-person-x" style={{ fontSize: 36 }}></i>
                              <span style={{ fontSize: 13 }}>Bu dərsdə tələbə qeyd edilməyib.</span>
                            </div>
                          ) : (
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                              <thead>
                                <tr style={{ background: "#f3f4f6" }}>
                                  {["#", "Ad Soyad", "Müəllim qeydi"].map((h, i) => (
                                    <th key={i} style={{ padding: "7px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb", color: "#374151", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {selectedHelpLesson.students.map((s, i) => (
                                  <tr key={s.contractId ?? i} style={{ borderBottom: "1px solid #e5e7eb", background: i % 2 === 0 ? "#fff" : "#fafafa" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "rgba(37,99,235,0.05)"}
                                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafafa"}>
                                    <td style={{ padding: "7px 12px", color: "#9ca3af", fontWeight: 600 }}>{i + 1}</td>
                                    <td style={{ padding: "7px 12px", color: "#111827", fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer" }} onClick={() => setHelpLessonCopiedText(`${s.name} ${s.surname}`)}>
                                      {s.name} {s.surname}
                                    </td>
                                    <td style={{ padding: "7px 12px", color: s.teacherNotes ? "#374151" : "#d1d5db", fontStyle: s.teacherNotes ? "normal" : "italic", cursor: s.teacherNotes ? "pointer" : "default" }} onClick={() => s.teacherNotes && setHelpLessonCopiedText(s.teacherNotes)}>
                                      {s.teacherNotes || "Qeyd yoxdur"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
            <CopyFooter value={activeTab === "Kömək dərslərim" ? helpLessonCopiedText : copiedText} />
          </div>
        </main>

        {!lessonActive && (
          <div className={styles.startLessonFab}>
            <Button
              className={styles.btnStartLesson}
              onClick={openStartLessonConfirm}
              disabled={lessonLoading}
            >
              {lessonLoading ? (
                <>
                  <i className="bi bi-arrow-clockwise"></i> Gözləyin...
                </>
              ) : (
                <>
                  <i className="bi bi-play-fill"></i> Dərsi başlat
                </>
              )}
            </Button>
          </div>
        )}

        <Modal
          show={showStartLessonConfirm}
          onHide={() => setShowStartLessonConfirm(false)}
          centered
          backdrop="static"
          enforceFocus={false}
          size="sm"
        >
          <Modal.Body style={{ padding: "30px 20px", textAlign: "center" }}>
            <div style={{
              width: "60px", height: "60px", background: "rgba(34, 197, 94, 0.1)",
              borderRadius: "50%", display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 20px"
            }}>
              <i className="bi bi-play-circle" style={{ fontSize: "30px", color: "#22c55e" }}></i>
            </div>
            <h5 style={{ fontWeight: 700, fontSize: "16px", marginBottom: "10px", color: "#111827" }}>Dərsi başlatmağa hazırsınız?</h5>
            <p style={{ color: "#4b5563", fontSize: "13px", lineHeight: "1.5" }}>
Dərsi başlatmaq üçün təsdiqləyin. Bu əməliyyat geri qaytarıla bilməz.
            </p>
            <div style={{ display: "flex", gap: "10px", marginTop: "25px" }}>
              <Button
                variant="light"
                onClick={() => setShowStartLessonConfirm(false)}
                disabled={lessonLoading}
                style={{ flex: 1, fontSize: "13px", fontWeight: 600, borderRadius: "8px", padding: "10px" }}
              >
                Ləğv et
              </Button>
              <Button
                onClick={confirmAndStartLesson}
                disabled={lessonLoading}
                style={{ flex: 1, fontSize: "13px", fontWeight: 600, borderRadius: "8px", padding: "10px", background: "#22c55e", border: "none", color: "#fff" }}
              >
                {lessonLoading ? "..." : "Bəli, başlat"}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
      <ValidationErrorModal show={!!errorModal} onHide={clearError} message={errorModal?.message} validations={errorModal?.validations} />
    </Modal>
  );
};

export default GroupEntryModal;
